/**
 * Logger Utility
 * ログ出力の統一的な管理
 */

import chalk from "chalk";

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

export class Logger {
  private level: LogLevel = LogLevel.WARN; // Default to WARN to reduce noise
  private readonly prefix = "[MARIA CODE]";
  private format: 'pretty' | 'json' = 'pretty';

  setLevel(level: LogLevel): void {
    this.level = level;
  }

  setFormat(format: 'pretty' | 'json'): void {
    this.format = format;
  }

  private out(level: keyof Console, tag: string, args: unknown[]): void {
    if (this.format === 'json') {
      const payload = {
        ts: new Date().toISOString(),
        level: tag.replace(/\[|\]/g, '').toLowerCase(),
        msg: args.map(String).join(' '),
      };
      // eslint-disable-next-line no-console
      console[level](JSON.stringify(payload));
      return;
    }
    // pretty
    // eslint-disable-next-line no-console
    console[level](tag, ...args);
  }

  debug(...args: unknown[]): void {
    if (this.level <= LogLevel.DEBUG) {
      this.out('log', chalk.magenta(`${this.prefix} [DEBUG]`), args);
    }
  }

  info(...args: unknown[]): void {
    if (this.level <= LogLevel.INFO) {
      this.out('log', chalk.bold.magenta(`${this.prefix} [INFO]`), args);
    }
  }

  warn(...args: unknown[]): void {
    if (this.level <= LogLevel.WARN) {
      this.out('warn', chalk.bold.magenta(`${this.prefix} [WARN]`), args as unknown[]);
    }
  }

  error(...args: unknown[]): void {
    if (this.level <= LogLevel.ERROR) {
      this.out('error', chalk.bold.magenta(`${this.prefix} [ERROR]`), args as unknown[]);
    }
  }

  success(...args: unknown[]): void {
    if (this.level <= LogLevel.INFO) {
      this.out('log', chalk.bold.magenta(`${this.prefix} [SUCCESS]`), args);
    }
  }

  task(
    taskName: string,
    status: "start" | "progress" | "complete" | "error",
    message?: string,
  ): void {
    if (this.level > LogLevel.INFO) {
      return;
    }

    const statusIcons = {
      start: "",
      progress: "⏳",
      complete: "",
      error: "",
    };

    const statusColors = {
      start: chalk.bold.magenta,
      progress: chalk.magenta,
      complete: chalk.bold.magenta,
      error: chalk.bold.magenta,
    };

    const icon = statusIcons[status];
    const color = statusColors[status];
    const formattedMessage = message ? `: ${message}` : "";

    console.log(color(`${this.prefix} ${icon} ${taskName}${formattedMessage}`));
  }

  table(data: Record<string, unknown>[]): void {
    if (this.level > LogLevel.INFO) {
      return;
    }
    console.table(data);
  }

  json(obj: unknown, pretty = true): void {
    if (this.level > LogLevel.DEBUG) {
      return;
    }
    if (this.format === 'json') {
      this.out('log', '', [JSON.stringify(obj)]);
    } else {
      // eslint-disable-next-line no-console
      console.log(chalk.magenta(`${this.prefix} [JSON]`));
      // eslint-disable-next-line no-console
      console.log(pretty ? JSON.stringify(obj, null, 2) : JSON.stringify(obj));
    }
  }

  divider(): void {
    if (this.level > LogLevel.INFO) {
      return;
    }
    console.log(chalk.magenta("─".repeat(60)));
  }

  clear(): void {
    console.clear();
  }

  /**
   * プログレスバーを表示
   */
  progress(current: number, total: number, label?: string): void {
    if (this.level > LogLevel.INFO) {
      return;
    }

    const percentage = Math.round((current / total) * 100);
    const barLength = 30;
    const filled = Math.round((percentage / 100) * barLength);
    const empty = barLength - filled;

    const bar = "█".repeat(filled) + "░".repeat(empty);
    const progressText = `${current}/${total}`;
    const labelText = label ? ` ${label}` : "";

    process.stdout.write(
      `\r${chalk.bold.magenta(bar)} ${percentage}% ${progressText}${labelText}`,
    );

    if (current === total) {
      process.stdout.write("\n");
    }
  }
}

// シングルトンインスタンスをエクスポート
export const logger = new Logger();

// 環境変数でログレベルを設定
const envLogLevel = process.env["MARIA_LOG_LEVEL"]?.toUpperCase();
if (
  envLogLevel &&
  LogLevel[envLogLevel as keyof typeof LogLevel] !== undefined
) {
  logger.setLevel(LogLevel[envLogLevel as keyof typeof LogLevel]);
}

// 環境変数でログフォーマットを設定（json|pretty）
const envLogFormat = process.env["MARIA_LOG_FORMAT"]?.toLowerCase();
if (envLogFormat === 'json' || envLogFormat === 'pretty') {
  logger.setFormat(envLogFormat);
}
